import React from 'react'
import {FormattedMessage} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {getScrollTargetSpecificRoom} from 'hooks/useHandleScrollToClickedOfferTarget/utils'
import {isRoomsViewExpanded} from 'modules/accommodation/selectors'
import {collapseRoomsView, expandRoomsView} from 'modules/accommodation/slice'
import {getFilteredRoomsData} from 'modules/searchApi/selectors'

import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {Text} from '@vio/atlas/Text'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'

import {COMPACT_ROOMS_AMOUNT} from '../constants'

interface Props {
  isLoading: boolean
}

export const RoomsSectionFooter = ({isLoading}: Props) => {
  const dispatch = useDispatch()
  const {splitBooking, rooms, totalResults} = useSelector(getFilteredRoomsData)
  const isListExpanded = useSelector(isRoomsViewExpanded)
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const isMobileOrTablet = isMobile || isDesktopXs
  const filteredRoomsLength = rooms.length + (splitBooking ? 1 : 0)

  const handleCollapseAndScroll = () => {
    dispatch(collapseRoomsView())

    // Access to the last unexpanded room's ID
    const lastVisibleRoomId = rooms[COMPACT_ROOMS_AMOUNT - 1].id

    const element = document.getElementById(
      getScrollTargetSpecificRoom(lastVisibleRoomId)
    )

    setTimeout(() => {
      element?.scrollIntoView({behavior: 'smooth'})
    })
  }
  const toggleExpandRoomsList = () => {
    if (!isListExpanded) {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.SeeMoreRoomsButton,
          action: Action.Clicked,
          team: Team.Select
        })
      )

      dispatch(expandRoomsView())
    } else {
      handleCollapseAndScroll()
    }
  }
  if (isLoading && !isMobileOrTablet)
    return (
      <Box paddingTop="s300">
        <Box display="flex" justifyContent="center">
          <SkeletonLoader width={365} />
        </Box>
      </Box>
    )
  if (!totalResults) return null

  return (
    <Box paddingTop="s300">
      <Box display="flex" justifyContent="center">
        {filteredRoomsLength > COMPACT_ROOMS_AMOUNT ? (
          <Button
            variant="tertiary"
            onClick={toggleExpandRoomsList}
            iconEnd={
              <Icon name={isListExpanded ? 'ChevronUp' : 'ChevronDown'} />
            }
            dataId="roomSectionFooterButton"
          >
            {isListExpanded ? (
              <FormattedMessage
                id="roomsList.collapseRoomsList"
                defaultMessage="Hide room options"
              />
            ) : (
              <FormattedMessage
                id="roomsList.expandRoomsList"
                defaultMessage="Show more rooms"
              />
            )}
          </Button>
        ) : (
          <Text
            align="center"
            variant="bodyS"
            colorPath="content.neutral.secondary"
          >
            <FormattedMessage
              id="noMoreRoomsToShowLabel"
              defaultMessage="No more rooms to show"
            />
          </Text>
        )}
      </Box>
    </Box>
  )
}
